"use client";

/**
 * Global error boundary for the storefront. It REPLACES the root layout when a
 * render crash occurs (server or client), so it must render its own
 * <html>/<body> — the [locale] layout never runs in this path, which means no
 * locale/dir/fonts context is available. It therefore stays English / LTR;
 * acceptable for a crash page.
 *
 * Renders a centred Card with `<EmptyState>` framing + a "Try again" button
 * wired to Next.js's `reset()` recovery callback.
 *
 * Production trust posture (P11-T9 follow-up): in production we ship generic
 * copy plus `error.digest` (Next's correlation id, also indexed in Sentry)
 * so support can look the incident up — we deliberately do NOT echo the raw
 * exception `.message`, which can leak api detail / SQL / file paths into
 * the buyer-facing surface. In development the raw message is shown verbatim
 * so engineers can see what blew up without round-tripping through Sentry.
 */
import { Button, Card, EmptyState, Logo } from "@wmart/ui";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isDev = process.env.NODE_ENV === "development";
  const message =
    isDev && error.message ? error.message : "An unexpected error occurred.";

  return (
    <html lang="en">
      <body className="bg-paper text-ink-700 antialiased">
        <div className="mx-auto max-w-md px-6 py-16">
          <Card className="p-8">
            <div className="mb-6 flex justify-center">
              <a
                href="/"
                className="rounded-sm outline-none focus-visible:shadow-focus"
              >
                <Logo size="md" />
              </a>
            </div>
            <EmptyState
              icon="alert"
              title="Something went wrong"
              body={
                <>
                  {message}
                  {error.digest ? (
                    <span className="mt-1 block text-caption text-ink-500">
                      Ref: {error.digest}
                    </span>
                  ) : null}
                </>
              }
              action={
                <Button variant="primary" size="md" onClick={reset}>
                  Try again
                </Button>
              }
            />
          </Card>
        </div>
      </body>
    </html>
  );
}
